import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import LinearProgress from "@mui/material/LinearProgress";
import Pagination from "@mui/material/Pagination";

import { get } from "lib/api";
import MobileTopBar from "mobile/components/MobileTopBar";
import { IosList, IosListItem, IosPage } from "mobile/components/IosParity";

const normalize = (d) =>
  d?.attributes ? { id: d.id, documentId: d.documentId || d.id, ...d.attributes } : { ...d, documentId: d.documentId || d.id };

export default function VendorsMobile() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 25;

  useEffect(() => setPage(1), [search]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["m-vendors", search, page, pageSize],
    queryFn: () =>
      get("/api/vendors", {
        populate: "*",
        sort: "name:asc",
        pagination: { page, pageSize },
        filters: {
          ...(search && {
            $or: [
              { name: { $containsi: search } },
              { vendorNumber: { $containsi: search } },
              { contact: { $containsi: search } },
            ],
          }),
        },
      }),
    staleTime: 5_000,
  });

  const rows = useMemo(() => (data?.data || []).map(normalize), [data]);
  const pageCount = data?.meta?.pagination?.pageCount || 1;
  const total = data?.meta?.pagination?.total;

  return (
    <IosPage>
      <MobileTopBar
        title="Vendors"
        subtitle={typeof total === "number" ? `${total} total • page ${page}/${pageCount}` : "Suppliers"}
        onBack={() => navigate("/m/dashboard")}
        right={
          <IconButton aria-label="Refresh" size="small" onClick={() => qc.invalidateQueries({ queryKey: ["m-vendors"] })}>
            <Icon fontSize="small">refresh</Icon>
          </IconButton>
        }
      />

      {(isLoading || isFetching) && <LinearProgress sx={{ mt: 1 }} />}

      <Stack spacing={0.75} sx={{ mt: 1 }}>
        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name / vendor # / contact…"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon fontSize="small">search</Icon>
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton aria-label="Clear" size="small" onClick={() => setSearch("")}>
                  <Icon fontSize="small">close</Icon>
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        <IosList>
          {rows.map((v) => {
            const documentId = v.documentId;
            const emails = Array.isArray(v.emails) ? v.emails.filter(Boolean) : [];
            const phones = Array.isArray(v.phones) ? v.phones.filter(Boolean) : [];
            const terms = v.paymentTerms ? String(v.paymentTerms) : null;
            return (
              <IosListItem
                key={String(documentId || v.id)}
                icon="local_shipping"
                title={v.name || "Vendor"}
                meta={`Vendor ${v.vendorNumber || "—"} • ${v.contact || emails[0] || phones[0] || "—"}`}
                trailing={terms || "supplier"}
                onClick={() => {
                  if (documentId) navigate(`/m/vendors/${documentId}`);
                }}
              />
            );
          })}
          {!isLoading && rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No vendors found.
            </Typography>
          ) : null}
        </IosList>

        {pageCount > 1 ? (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 0.5 }}>
            <Pagination count={pageCount} page={page} onChange={(e, p) => setPage(p)} />
          </Box>
        ) : null}
      </Stack>
    </IosPage>
  );
}
